import { z } from "zod";

// Base offer schema - common fields for all offer types
export const baseOfferSchema = z.object({
    name: z.string().min(1, "يرجى إدخال اسم العرض"),
    start_date: z.string().min(1, "يرجى إدخال تاريخ البداية"),
    end_date: z.string().min(1, "يرجى إدخال تاريخ النهاية"),
    promotion_type_id: z.number().min(1, "يرجى اختيار نوع العرض"),
    target_audience: z.enum(["all", "new_users", "logged_in_only"]).default("all"),
    is_super_offer: z.boolean().default(false),
    is_active: z.enum(["0", "1"]).default("1"),
    store_id: z.number().nullable().optional(),
});

// Bundle offer specific schema
export const bundleSchema = z.object({
    name: z.string().min(1, "اسم الحزمة إجباري"),
    category_id: z.number().optional().nullable().default(null),
    min_selections: z.number().min(0),
    max_selections: z.number().min(1),
    bundle_products: z
        .array(
            z.object({
                product_id: z.number(),
                is_required: z.number().int().min(0).max(1).default(0),
                default_quantity: z.number().min(1),
                min_quantity: z.number().min(1),
                max_quantity: z.number().min(1),
            })
        )
        .min(1, "يجب اختيار منتج واحد على الأقل للحزمة"),
});

// XY offer products schema
export const xyProductSchema = z.object({
    product_id: z.number(),
    min_quantity: z.number().min(1),
    max_quantity: z.number().nullable().optional(),
});

// Coupon schema
export const couponSchema = z.object({
    code: z.string().min(1, "يرجى إدخال كود الكوبون"),
    usage_limit: z.number().optional(),
    usage_per_user: z.number().optional(),
});

// Purchase reward schema
export const purchaseRewardSchema = z.object({
    amount: z.number().min(1, "يرجى إدخال المبلغ"),
});

// Loyalty reward schema
export const loyaltyRewardSchema = z.object({
    name: z.string().min(1, "يرجى إدخال اسم عرض الولاء"),
    points_required: z.number().min(1, "يرجى إدخال عدد النقاط المطلوبة بشكل صحيح"),
    coupon: z.string().optional(),
    loyalty_tier_ids: z
        .array(z.number())
        .min(1, "يرجى اختيار مستوى ولاء واحد على الأقل"),
});

// Gift types schema
export const giftTypeSchema = z.object({
    gift_type_id: z.number(),
    value: z.number().optional(),
    percent: z.number().optional(),
});

// Free products schema
export const freeProductSchema = z.object({
    product_id: z.number(),
    quantity: z.number().min(1),
});

// Rewards schema (common for all offer types)
export const rewardsSchema = z
    .object({
        promotion_gift_types: z.array(giftTypeSchema).optional(),
        free_products: z.array(freeProductSchema).optional(),
    })
    .refine(
        (data) =>
            (data.promotion_gift_types && data.promotion_gift_types.length > 0) ||
            (data.free_products && data.free_products.length > 0),
        {
            message: "يرجى اختيار نوع مكافأة واحد على الأقل",
        }
    );

// Constraints schema
export const constraintSchema = z.object({
    type: z.string(),
    value: z.any().optional(),
    start_time: z.string().optional(),
    end_time: z.string().optional(),
});

// Exclusions schema
export const exclusionSchema = z.object({
    excluded_promotion_id: z.string(),
});

// Full offer schema with discriminated union based on offer type
export const offerSchema = z
    .object({
        // Base fields
        ...baseOfferSchema.shape,

        // Constraints and exclusions
        promotion_constraints: z.array(constraintSchema).optional(),
        promotion_exclusions: z.array(exclusionSchema).optional(),

        // Bundle offer fields (type 1)
        bundle: bundleSchema.optional(),

        // XY offer fields (type 2)
        buy_x_get_y_offer: z
            .object({
                buy_x_get_y_products: z.array(xyProductSchema).min(1),
            })
            .optional(),

        // Coupon fields (type 3)
        coupon: couponSchema.optional(),

        // Purchase reward fields (type 4)
        purchase_rewards: z.array(purchaseRewardSchema).optional(),

        // Loyalty reward fields (type 6)
        loyalty_reward: z
            .object({
                name: z.string(),
                points_required: z.number(),
                coupon: z.string().optional(),
            })
            .optional(),
        promotion_loyalty_tiers: z
            .array(
                z.object({
                    loyalty_tier_id: z.string(),
                })
            )
            .optional(),

        // Rewards (common)
        rewards: rewardsSchema.optional(),
    })
    .superRefine((data, ctx) => {
        // Validate based on offer type
        const offerType = data.promotion_type_id;

        // Bundle offer validation (type 1)
        if (offerType === 1) {
            if (!data.bundle?.name) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "اسم الحزمة إجباري",
                    path: ["bundle", "name"],
                });
            }
            if (!data.bundle?.bundle_products?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يجب اختيار منتج واحد على الأقل للحزمة",
                    path: ["bundle", "bundle_products"],
                });
            }
        }

        // XY offer validation (type 2)
        if (offerType === 2) {
            if (!data.buy_x_get_y_offer?.buy_x_get_y_products?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يجب اختيار منتج واحد على الأقل للعرض",
                    path: ["buy_x_get_y_offer", "buy_x_get_y_products"],
                });
            }
            if (
                !data.rewards?.promotion_gift_types?.length &&
                !data.rewards?.free_products?.length
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يجب اختيار نوع واحد من المكافآت على الأقل",
                    path: ["rewards"],
                });
            }
        }

        // Coupon validation (type 3)
        if (offerType === 3) {
            if (!data.coupon?.code) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى إدخال كود الكوبون",
                    path: ["coupon", "code"],
                });
            }
            if (
                !data.rewards?.promotion_gift_types?.length &&
                !data.rewards?.free_products?.length
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى اختيار نوع مكافأة واحد على الأقل",
                    path: ["rewards"],
                });
            }
        }

        // Purchase reward validation (type 4)
        if (offerType === 4) {
            if (!data.purchase_rewards?.[0]?.amount) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى إدخال المبلغ",
                    path: ["purchase_rewards"],
                });
            }
            if (
                !data.rewards?.promotion_gift_types?.length &&
                !data.rewards?.free_products?.length
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى اختيار نوع مكافأة واحد على الأقل",
                    path: ["rewards"],
                });
            }
        }

        // Loyalty reward validation (type 6)
        if (offerType === 6) {
            if (!data.loyalty_reward?.name) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى إدخال اسم عرض الولاء",
                    path: ["loyalty_reward", "name"],
                });
            }
            if (
                !data.loyalty_reward?.points_required ||
                data.loyalty_reward.points_required <= 0
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى إدخال عدد النقاط المطلوبة بشكل صحيح",
                    path: ["loyalty_reward", "points_required"],
                });
            }
            if (!data.promotion_loyalty_tiers?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى اختيار مستوى ولاء واحد على الأقل",
                    path: ["promotion_loyalty_tiers"],
                });
            }
            if (
                !data.rewards?.promotion_gift_types?.length &&
                !data.rewards?.free_products?.length
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "يرجى اختيار نوع مكافأة واحد على الأقل",
                    path: ["rewards"],
                });
            }
        }

        // Date validation
        if (data.start_date && data.end_date) {
            const startDate = new Date(data.start_date);
            const endDate = new Date(data.end_date);
            if (endDate < startDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "تاريخ النهاية يجب أن يكون بعد تاريخ البداية",
                    path: ["end_date"],
                });
            }
        }
    });

// Default values for the form
export const getDefaultOfferValues = () => {
    const today = new Date().toISOString().split("T")[0];
    return {
        name: "",
        start_date: today,
        end_date: "",
        promotion_type_id: 1,
        target_audience: "all",
        is_super_offer: false,
        is_active: "1",
        store_id: null,
        promotion_constraints: [],
        promotion_exclusions: [],
        bundle: {
            name: "",
            bundle_products: [],
        },
        buy_x_get_y_offer: {
            buy_x_get_y_products: [],
        },
        coupon: {
            code: "",
            usage_limit: undefined,
            usage_per_user: undefined,
        },
        purchase_rewards: [{ amount: 0 }],
        loyalty_reward: {
            name: "",
            points_required: 0,
            coupon: "",
        },
        promotion_loyalty_tiers: [],
        rewards: {
            promotion_gift_types: [],
            free_products: [],
        },
    };
};
